import { useUser } from "@clerk/clerk-react";
import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/services/api";
import type { UserProfile } from "@/types";

interface UserProfileContextType {
  userProfile: UserProfile | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

const UserProfileContext = createContext<UserProfileContextType | null>(null);

export function UserProfileProvider({ children }: { children: ReactNode }) {
  const { user, isLoaded: isClerkLoaded } = useUser();
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchOrCreateProfile = useCallback(async () => {
    if (!isClerkLoaded) return;

    if (!user) {
      setUserProfile(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      let profile = await api.getUserByClerkId(user.id);

      if (!profile) {
        const username = user.fullName || user.emailAddresses[0]?.emailAddress || "User";
        profile = await api.createUser({
          clerk_user_id: user.id,
          username,
          role: "user",
          email: user.emailAddresses[0]?.emailAddress,
          first_name: user.firstName ?? undefined,
          last_name: user.lastName ?? undefined,
        });
      }

      setUserProfile(profile);
    } catch (err) {
      console.error("Failed to fetch/create user profile:", err);
      setError(err instanceof Error ? err.message : "Failed to load user profile");
      setUserProfile(null);
    } finally {
      setIsLoading(false);
    }
  }, [isClerkLoaded, user]);

  useEffect(() => {
    fetchOrCreateProfile();
  }, [fetchOrCreateProfile]);

  return (
    <UserProfileContext.Provider
      value={{ userProfile, isLoading, error, refetch: fetchOrCreateProfile }}
    >
      {children}
    </UserProfileContext.Provider>
  );
}

export function useUserProfile() {
  const context = useContext(UserProfileContext);
  if (!context) {
    throw new Error("useUserProfile must be used within UserProfileProvider");
  }
  return context;
}
